'use client';

import { Button, Input, Textarea } from '@commenda/kakashi';
import { Plus, Trash2 } from 'lucide-react';
import type { SelectOption } from '@commenda/types';

export function OptionsEditor({
  options,
  onChange,
}: {
  options: SelectOption[];
  onChange: (options: SelectOption[]) => void;
}) {
  const addOption = () => {
    onChange([
      ...options,
      {
        value: `option${options.length + 1}`,
        label: `Option ${options.length + 1}`,
      },
    ]);
  };

  const updateOption = (index: number, updates: Partial<SelectOption>) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], ...updates };
    onChange(newOptions);
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="txt-small-medium text-text-secondary">Options</h3>
        <Button type="button" variant="tertiary" size="sm" onClick={addOption}>
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      <div className="space-y-2">
        {options.map((option, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="flex items-start gap-2 p-3 rounded-lg border border-border-secondary bg-bg-secondary"
          >
            <div className="flex-1 space-y-2">
              <Input
                value={option.label}
                onChange={(e) => updateOption(index, { label: e.target.value })}
                placeholder="Label"
              />
              <Input
                value={option.value}
                onChange={(e) => updateOption(index, { value: e.target.value })}
                placeholder="Value"
              />
              <Textarea
                value={option.helpText || ''}
                onChange={(e) =>
                  updateOption(index, {
                    helpText: e.target.value || undefined,
                  })
                }
                placeholder="Description (optional)"
                rows={2}
              />
            </div>
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => removeOption(index)}
              className="shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}

        {options.length === 0 && (
          <div className="text-center py-4 border border-dashed border-border-secondary rounded-lg">
            <p className="txt-small-regular text-text-tertiary">
              No options yet. Add one to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
